import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { cn } from '../lib/utils';

// Touch-friendly Link component for iOS compatibility
function TouchLink({ to, className, children, ...props }) {
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(to);
  };
  
  return (
    <Link
      to={to}
      className={className}
      onClick={handleClick}
      {...props}
    >
      {children}
    </Link>
  );
}

// Reusable Hero Section
export function PageHero({ 
  badge, 
  title, 
  subtitle, 
  description, 
  primaryCTA, 
  secondaryCTA,
  image,
  gradient = 'from-slate-50 via-white to-slate-100'
}) {
  return (
    <section className={`bg-gradient-to-br ${gradient} py-12 sm:py-16 lg:py-24`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="text-center lg:text-left">
            {badge && (
              <span className="inline-block px-3 py-1 text-xs sm:text-sm font-semibold text-primary bg-primary/10 rounded-full mb-4">
                {badge}
              </span>
            )}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight leading-tight mb-4">
              {title}
              {subtitle && (
                <span className="text-primary block mt-2">{subtitle}</span>
              )}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-6 sm:mb-8">
              {description}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
              {primaryCTA && (
                <TouchLink
                  to={primaryCTA.href}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold text-white bg-primary rounded-xl hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 touch-manipulation cursor-pointer"
                >
                  {primaryCTA.label}
                  <ArrowRight className="w-4 h-4" />
                </TouchLink>
              )}
              {secondaryCTA && (
                <TouchLink
                  to={secondaryCTA.href}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold text-primary border-2 border-primary rounded-xl hover:bg-primary/5 transition-colors touch-manipulation cursor-pointer"
                >
                  {secondaryCTA.label}
                </TouchLink>
              )}
            </div>
          </div>
          {image && (
            <div className="hidden lg:block">
              <img src={image} alt="" className="w-full rounded-2xl shadow-2xl" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// How It Works Section
export function HowItWorks({ title = "How It Works", steps }) {
  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-8 sm:mb-12">
          {title}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <span className="text-lg sm:text-xl font-bold text-primary">{index + 1}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg mb-2">{step.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600">{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Benefits Grid
export function BenefitsGrid({ title, benefits }) {
  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {title && (
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-8 sm:mb-12">
            {title}
          </h2>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={cn(
                "bg-white rounded-xl p-5 sm:p-6 shadow-sm border transition-shadow hover:shadow-md",
                benefit.highlight && "ring-2 ring-primary/20"
              )}
            >
              {benefit.icon && (
                <div className={cn(
                  "w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-4",
                  benefit.highlight ? "bg-primary text-white" : "bg-slate-100 text-slate-600"
                )}>
                  <benefit.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
              )}
              <h3 className="font-semibold text-base sm:text-lg mb-2">{benefit.title}</h3>
              <p className="text-sm sm:text-base text-slate-600">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// FAQ Section
export function FAQSection({ title = "Frequently Asked Questions", faqs }) {
  const [openIndex, setOpenIndex] = React.useState(null);

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-8 sm:mb-12">
          {title}
        </h2>
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div key={index} className="border rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-4 sm:px-6 py-4 text-left flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors"
              >
                <span className="font-medium text-sm sm:text-base">{faq.question}</span>
                <span className={cn(
                  "flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center transition-transform",
                  openIndex === index && "rotate-180"
                )}>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </button>
              <div className={cn(
                "overflow-hidden transition-all duration-200",
                openIndex === index ? "max-h-96" : "max-h-0"
              )}>
                <div className="px-4 sm:px-6 pb-4 text-sm sm:text-base text-slate-600">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// CTA Section
export function CTASection({ title, description, primaryCTA, secondaryCTA, variant = 'primary' }) {
  const bgClass = variant === 'primary' 
    ? 'bg-primary text-white' 
    : 'bg-slate-900 text-white';

  return (
    <section className={`${bgClass} py-12 sm:py-16 lg:py-20`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold mb-4">
          {title}
        </h2>
        <p className="text-base sm:text-lg opacity-90 mb-6 sm:mb-8 max-w-2xl mx-auto">
          {description}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          {primaryCTA && (
            <TouchLink
              to={primaryCTA.href}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold bg-white text-primary rounded-xl hover:bg-slate-100 transition-colors touch-manipulation cursor-pointer"
            >
              {primaryCTA.label}
              <ArrowRight className="w-4 h-4" />
            </TouchLink>
          )}
          {secondaryCTA && (
            <TouchLink
              to={secondaryCTA.href}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold border-2 border-white/30 rounded-xl hover:bg-white/10 transition-colors touch-manipulation cursor-pointer"
            >
              {secondaryCTA.label}
            </TouchLink>
          )}
        </div>
      </div>
    </section>
  );
}

// Feature List with checkmarks
export function FeatureList({ features }) {
  return (
    <ul className="space-y-3">
      {features.map((feature, index) => (
        <li key={index} className="flex items-start gap-3">
          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
            <Check className="w-3 h-3 text-green-600" />
          </div>
          <span className="text-sm sm:text-base text-slate-700">{feature}</span>
        </li>
      ))}
    </ul>
  );
}

export default { PageHero, HowItWorks, BenefitsGrid, FAQSection, CTASection, FeatureList };
